import useCommon from '@utils/hooks/common';
import {Button, InputNumber, notification} from 'antd';
import {useEffect, useRef, useState} from 'react';
import styles from './styles';
import {
  getMerchantOpenId,
  requestDeposit,
  requestWithdraw,
  directDeposit,
  directWithdraw,
  onGatewayMessages,
} from '@utils/gateway/merchant-gateway';

interface MerchantContentProps {
  mode: 'deposit' | 'withdraw';
}

const MerchantContent = ({mode}: MerchantContentProps) => {
  const {classes, t, user} = useCommon({
    withStyles: styles,
    withTranslation: ['deposit_withdraw'],
    observers: ['user'],
  });

  const [amount, setAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [quickLoading, setQuickLoading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const openId = getMerchantOpenId();
  const pendingOrderNo = useRef<string | null>(null);
  const pendingAmount = useRef<string | null>(null);

  const pokerBalance = user?.getCoinWalletBalance() || 0;

  // Only the poker balance is shown in the UI now; the merchant miniapp
  // balance fetch was removed when the miniapp-balance row was hidden.
  const refreshBalances = () => {
    user.refreshPlayerInfo();
  };

  // Listen for postMessage results from 88x parent
  useEffect(() => {
    const cleanup = onGatewayMessages({
      async onDepositResult(result) {
        if (result.success) {
          setStatus('Purchasing poker coins...');
          try {
            await directDeposit(openId, pendingAmount.current);
            notification.success({message: 'Deposit Successful'});
            refreshBalances();
            setAmount(null);
          } catch (err: any) {
            notification.error({
              message: 'Deposit Failed',
              description: 'Recharge succeeded but coin purchase failed: ' + err.message,
            });
          }
        } else {
          notification.error({
            message: 'Deposit Failed',
            description: result.message || 'Recharge cancelled',
          });
        }
        pendingOrderNo.current = null;
        pendingAmount.current = null;
        setLoading(false);
        setQuickLoading(false);
        setStatus(null);
      },
      async onWithdrawResult(result) {
        if (result.success) {
          notification.success({message: 'Withdraw Successful'});
          refreshBalances();
          setAmount(null);
        } else {
          notification.error({
            message: 'Withdraw Failed',
            description: result.message || 'Withdrawal to main wallet cancelled',
          });
        }
        pendingOrderNo.current = null;
        pendingAmount.current = null;
        setLoading(false);
        setQuickLoading(false);
        setStatus(null);
      },
    });

    return cleanup;
  }, [openId]);

  // Full deposit: 88x main → miniapp → poker coins
  const handleDeposit = async () => {
    if (!openId || !amount || amount <= 0) return;
    setLoading(true);
    setStatus('Waiting for 88x confirmation...');
    pendingAmount.current = amount.toString();
    try {
      const orderNo = await requestDeposit(openId, amount.toString());
      pendingOrderNo.current = orderNo;
    } catch (err: any) {
      setLoading(false);
      setStatus(null);
      pendingAmount.current = null;
      notification.error({message: 'Deposit Failed', description: err.message});
    }
  };

  // Full withdraw: poker coins → miniapp → 88x main
  const handleWithdraw = async () => {
    if (!openId || !amount || amount <= 0) return;
    setLoading(true);
    setStatus('Cashing out poker coins...');
    pendingAmount.current = amount.toString();
    try {
      await directWithdraw(openId, amount.toString());
      setStatus('Waiting for 88x confirmation...');
      const orderNo = await requestWithdraw(openId, amount.toString());
      pendingOrderNo.current = orderNo;
    } catch (err: any) {
      setLoading(false);
      setStatus(null);
      pendingAmount.current = null;
      notification.error({message: 'Withdraw Failed', description: err.message});
    }
  };

  // handlePurchaseAll / handleWithdrawAll quick-action handlers were
  // removed alongside the miniapp-balance display. Their JSX buttons
  // were already commented out below; resurrect them only when the
  // miniapp balance is shown again.


  if (!openId) {
    return <div>Not authenticated</div>;
  }

  const isAnyLoading = loading || quickLoading;

  return (
    <div className={classes.container} onClick={e => e.stopPropagation()}>
      <div className={classes.balanceRow}>
        <span className={classes.balanceLabel}>Poker Balance</span>
        <span className={classes.balanceValue}>{pokerBalance} USD</span>
      </div>

      {/* Quick actions — hidden for now */}
      {/* <div style={{display: 'flex', gap: 12, marginBottom: 16}}>
        <Button
          type="primary"
          size="large"
          block
          loading={quickLoading}
          disabled={isAnyLoading || parseFloat(balance) <= 0 || balance === '--'}
          onClick={handlePurchaseAll}
          style={{background: '#52c41a', borderColor: '#52c41a'}}
        >
          Purchase All Coins
        </Button>
        <Button
          size="large"
          block
          loading={quickLoading}
          disabled={isAnyLoading || pokerBalance <= 0}
          onClick={handleWithdrawAll}
          danger
        >
          Withdraw All
        </Button>
      </div>

      <Divider style={{borderColor: 'rgba(255,255,255,0.1)', margin: '12px 0'}} /> */}

      {/* Manual amount deposit/withdraw */}
      <div className={classes.inputGroup}>
        <span className={classes.label}>
          {mode === 'deposit' ? 'Deposit Amount' : 'Withdraw Amount'}
        </span>
        <InputNumber
          style={{
            width: '100%',
            background: 'rgba(255, 255, 255, 0.1)',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
          }}
          size="large"
          min={0.01}
          max={10000}
          precision={2}
          placeholder={
            mode === 'deposit' ? 'Enter deposit amount' : 'Enter withdraw amount'
          }
          value={amount}
          onChange={val => setAmount(val)}
          disabled={isAnyLoading}
        />
      </div>

      <div className={classes.buttonRow}>
        <Button
          type="primary"
          size="large"
          block
          loading={loading}
          disabled={isAnyLoading || !amount || amount <= 0}
          onClick={mode === 'deposit' ? handleDeposit : handleWithdraw}
        >
          {mode === 'deposit' ? 'Deposit' : 'Withdraw'}
        </Button>
      </div>

      {status && <div className={classes.statusText}>{status}</div>}
    </div>
  );
};

export default MerchantContent;
